import type {
  DailySummaryRecipientRequest,
  DefaultApi,
  WeeklySummaryRecipientRequest,
} from "@/api";
import { DailySummaryRecipient } from "@/models/daily_summary_recipient";
import { DailySummaryRecipients } from "@/models/daily_summary_recipients";
import { WeeklySummaryRecipient } from "@/models/weekly_summary_recipient";
import { WeeklySummaryRecipients } from "@/models/weekly_summary_recipients";
import type { State as RootState } from "@/stores";
import { type Module } from "vuex";
export interface State {
  dailySummaryRecipient?: DailySummaryRecipients;
  weeklySummaryRecipient?: WeeklySummaryRecipients;
}
export const module: Module<State, RootState> = {
  namespaced: true,
  state: {
    dailySummaryRecipient: undefined,
    weeklySummaryRecipient: undefined,
  },
  actions: {
    async getDailySummaryRecipients({ commit }) {
      try {
        const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
          root: true,
        });
        const response = await api.getDailySummaryRecipients();
        const dailySummaryRecipient = new DailySummaryRecipients(
          response.data.map(
            (recipient) => new DailySummaryRecipient(recipient),
          ),
        );
        commit("setDailySummaryRecipient", dailySummaryRecipient);
        return dailySummaryRecipient;
      } catch (error) {
        commit("setDailySummaryRecipient", undefined);
        throw error;
      }
    },
    async getWeeklySummaryRecipients({ commit }) {
      try {
        const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
          root: true,
        });
        const response = await api.getWeeklySummaryRecipients();
        const weeklySummaryRecipient = new WeeklySummaryRecipients(
          response.data.map(
            (recipient) => new WeeklySummaryRecipient(recipient),
          ),
        );
        commit("setWeeklySummaryRecipient", weeklySummaryRecipient);
        return weeklySummaryRecipient;
      } catch (error) {
        commit("setWeeklySummaryRecipient", undefined);
        throw error;
      }
    },
    async postDailySummaryRecipients(
      { commit },
      dailySummaryRecipientRequest: DailySummaryRecipientRequest[],
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postDailySummaryRecipients(
        dailySummaryRecipientRequest,
      );
      const dailySummaryRecipient = new DailySummaryRecipients(
        response.data.map((recipient) => new DailySummaryRecipient(recipient)),
      );
      commit("appendDailySummaryRecipient", dailySummaryRecipient);
      return dailySummaryRecipient;
    },
    async postWeeklySummaryRecipients(
      { commit },
      weeklySummaryRecipientRequest: WeeklySummaryRecipientRequest[],
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postWeeklySummaryRecipients(
        weeklySummaryRecipientRequest,
      );
      const weeklySummaryRecipient = new WeeklySummaryRecipients(
        response.data.map((recipient) => new WeeklySummaryRecipient(recipient)),
      );
      commit("appendWeeklySummaryRecipient", weeklySummaryRecipient);
      return weeklySummaryRecipient;
    },
  },
  mutations: {
    setDailySummaryRecipient(
      state,
      dailySummaryRecipient: DailySummaryRecipients,
    ) {
      state.dailySummaryRecipient = dailySummaryRecipient;
    },
    appendDailySummaryRecipient(
      state,
      dailySummaryRecipient: DailySummaryRecipients,
    ) {
      if (state.dailySummaryRecipient === undefined) {
        state.dailySummaryRecipient = dailySummaryRecipient;
      } else {
        state.dailySummaryRecipient.push(...dailySummaryRecipient);
      }
    },
    setWeeklySummaryRecipient(
      state,
      weeklySummaryRecipient: WeeklySummaryRecipients,
    ) {
      state.weeklySummaryRecipient = weeklySummaryRecipient;
    },
    appendWeeklySummaryRecipient(
      state,
      weeklySummaryRecipient: WeeklySummaryRecipients,
    ) {
      if (state.weeklySummaryRecipient === undefined) {
        state.weeklySummaryRecipient = weeklySummaryRecipient;
      } else {
        state.weeklySummaryRecipient.push(...weeklySummaryRecipient);
      }
    },
  },
};
